/** Fundamentals tab. Mock data shaped for the fields a later viaNexus Core
 * release would supply. Nothing here reads a provider.
 */
import { useState } from "react";
import {
  CachedBadge, DataNotice, EmptyNote, KeyRow, MockBadge, PanelGrid, SectionTitle, WorkspacePanel,
} from "@/workspace/primitives";
import { SimpleBarChart } from "@/workspace/charts";
import { fundamentals } from "@/workspace/mockTickerData";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";

function PeerRow({ m }) {
  const better = m.better === "up";
  return (
    <div className="flex items-center gap-2 text-[11px] py-1 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
      <span className="w-[34%] shrink-0 truncate" style={{ color: "var(--hoip-text-secondary)" }}>{m.label}</span>
      <span className="w-[22%] shrink-0 font-mono-data">{m.value}</span>
      <span className="w-[22%] shrink-0 font-mono-data text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        {m.peer}
      </span>
      <span className="font-mono-data text-[10px]" style={{ color: better ? "#10B981" : "#EF4444" }}>
        {better ? "▲ Ahead of sector" : "▼ Behind sector"}
      </span>
    </div>
  );
}

export function FundamentalsTab({ t }) {
  const f = t.live ? null : fundamentals(t.ticker);
  const live = t.fundamentalsLive || {};
  const [view, setView] = useState("quarterly");
  const trend = t.live ? [] : (view === "quarterly" ? f.quarterly : f.annual);

  if (t.live) {
    return (
      <div className="space-y-3">
        <WorkspacePanel title="Company profile" notice={<CachedBadge />} testid="fund-profile">
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.profile}</p>
          <div className="mt-2 space-y-0.5">
            <KeyRow label="Executives">{LIVE_UNAVAILABLE.executives}</KeyRow>
            <KeyRow label="Employees">{LIVE_UNAVAILABLE.employees}</KeyRow>
          </div>
        </WorkspacePanel>
        <WorkspacePanel title="Current fundamentals snapshot" notice={<CachedBadge />} testid="fund-snapshot">
          <KeyRow label="Fundamental score">{t.fundamentalScore ?? "—"}</KeyRow>
          <KeyRow label="Sector">{t.sector || "—"}</KeyRow>
          <KeyRow label="P/E">{live.pe ?? "Not available in current cached output"}</KeyRow>
          <KeyRow label="Forward P/E">{live.forwardPe ?? "Not available in current cached output"}</KeyRow>
          <KeyRow label="Revenue growth">{live.revenueGrowth ?? "Not available in current cached output"}</KeyRow>
          <KeyRow label="Earnings growth">{live.earningsGrowth ?? "Not available in current cached output"}</KeyRow>
          <KeyRow label="Debt / equity">{live.debtEquity ?? "Not available in current cached output"}</KeyRow>
          <KeyRow label="Analyst mean target">{live.targetMean ?? "Not available in current cached output"}</KeyRow>
          <KeyRow label="Analyst count">{live.analystCount ?? "Not available in current cached output"}</KeyRow>
          <p className="text-[10px] mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
            Analyst targets are reference-only when present. Fetch time is not market observation time.
          </p>
        </WorkspacePanel>
        <WorkspacePanel title="Ticker versus sector" notice={<CachedBadge />} testid="fund-peers">
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.peers}</p>
        </WorkspacePanel>
        <WorkspacePanel title="Trend" notice={<CachedBadge />} testid="fund-trend">
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.statements}</p>
        </WorkspacePanel>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <WorkspacePanel
        title="Company profile"
        subtitle={`${t.companyName} · ${t.industry}`}
        notice={<MockBadge />}
        hint="Profile fields have no source today. Reserved for a later viaNexus Core release."
        testid="fund-profile"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 min-w-0">
            <SectionTitle>Business summary</SectionTitle>
            <p className="text-[11px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
              {f.profile.summary}
            </p>
          </div>
          <div className="min-w-0">
            <SectionTitle>Company</SectionTitle>
            <KeyRow label="Website">{f.profile.website}</KeyRow>
            <KeyRow label="Employees">{f.profile.employees.toLocaleString("en-US")}</KeyRow>
            <KeyRow label="Headquarters">{f.profile.headquarters}</KeyRow>
            <div className="mt-2">
              <SectionTitle>Executives</SectionTitle>
              {f.profile.executives.map((e) => (
                <div key={e.name} className="text-[10px] py-0.5">
                  <div>{e.name}</div>
                  <div style={{ color: "var(--hoip-text-tertiary)" }}>{e.role}</div>
                </div>
              ))}
            </div>
            <div
              className="mt-2 pt-2 border-t text-[9px]"
              style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}
            >
              Company logo slot reserved.
            </div>
          </div>
        </div>
      </WorkspacePanel>

      <WorkspacePanel
        title="Ticker versus sector"
        subtitle="Value · sector median · relative position"
        notice={<MockBadge />}
        hint="Peer medians are fixtures. A later release would compute them from the sector constituent set."
        testid="fund-peers"
      >
        <div className="flex items-center gap-2 text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
          <span className="w-[34%] shrink-0">Metric</span>
          <span className="w-[22%] shrink-0">{t.ticker}</span>
          <span className="w-[22%] shrink-0">Sector median</span>
          <span>Relative</span>
        </div>
        {f.metrics.map((m) => <PeerRow key={m.key} m={m} />)}
      </WorkspacePanel>

      <WorkspacePanel
        title="Trend"
        notice={<MockBadge />}
        actions={(
          <div className="flex gap-1">
            {["quarterly", "annual"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className="text-[9px] px-1.5 py-0.5 rounded-sm border capitalize"
                style={{
                  color: view === v ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                  background: view === v ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                  borderColor: view === v ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                }}
              >
                {v}
              </button>
            ))}
          </div>
        )}
        testid="fund-trend"
      >
        {trend.length ? (
          <PanelGrid cols={3}>
            <div>
              <SectionTitle>Revenue ($B)</SectionTitle>
              <SimpleBarChart data={trend} dataKey="revenue" height={150} testid="fund-revenue-chart" />
            </div>
            <div>
              <SectionTitle>EPS ($)</SectionTitle>
              <SimpleBarChart data={trend} dataKey="eps" height={150} testid="fund-eps-chart" />
            </div>
            <div>
              <SectionTitle>Operating margin (%)</SectionTitle>
              <SimpleBarChart data={trend} dataKey="margin" height={150} testid="fund-margin-chart" />
            </div>
          </PanelGrid>
        ) : (
          <EmptyNote>No trend series for this view.</EmptyNote>
        )}
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <DataNotice adminText="Today the workbench reads a narrow fundamentals snapshot (pe_ratio, revenue_growth, earnings_growth, debt_equity, fundamental_score). The wider metric set on this tab has no source yet." />
        </div>
      </WorkspacePanel>
    </div>
  );
}
